import 'react-tabs/style/react-tabs.css'
import classes from './VerticalTabs.module.scss'
import {
  Children,
  useMemo
} from 'react'
import {
  Tabs as ReactTabs,
  Tab,
  TabList,
  TabPanel
} from 'react-tabs'
import type { ReactNode } from 'react'

export interface BaseProps {
  children: ReactNode[]
  tabLabels: ReactNode[]
  isDisabled?: boolean
  showTabLabels?: boolean
  externalQuery?: ReactNode
}

export interface VisibilityPanelProps extends BaseProps {
  onTabIndexChange: (value: number) => void
  selectedTabIndex: number
  isInvisiblePanel: true
}

export interface NonInvisibleProps extends BaseProps {
  onTabIndexChange?: (value: number) => void
  selectedTabIndex?: number
  isInvisiblePanel?: false
}

type Props = NonInvisibleProps | VisibilityPanelProps

const visibilityPanelStyle = (tabIndex: number, selectedTab?: number) => tabIndex === selectedTab
  ? classes.TabPanelChildrenVisible
  : classes.TabPanelChildrenInvisible

const VerticalTabs = ({
  children,
  tabLabels,
  showTabLabels = true,
  onTabIndexChange,
  selectedTabIndex,
  isDisabled = false,
  isInvisiblePanel = false,
  externalQuery
}: Props) => {
  const tabData = useMemo(() => {
    const childrenArray = Children.toArray(children)

    return tabLabels.map((label, index) => ({
      label,
      child: childrenArray[index]
    }))
  }, [tabLabels, children])

  const disabledStyle = isDisabled
    ? classes.DisabledTab
    : undefined

  const tabClassName = [
    classes.Tab,
    disabledStyle
  ].filter(tabClassNames => tabClassNames).join(' ')

  return (
    <ReactTabs
      className={classes.VerticalTabs}
      onSelect={
        onTabIndexChange
          ? (index: number) => onTabIndexChange(index)
          : undefined
      }
      selectedIndex={selectedTabIndex}
      selectedTabClassName={classes.TabSelected}
    >
      {
        showTabLabels &&
          <TabList className={classes.TabList}>
            {
              tabData.map((item, index) =>
                <Tab
                  className={tabClassName}
                  disabled={isDisabled}
                  key={index}
                >
                  {item.label}
                </Tab>
              )
            }
            {
              externalQuery
                ? (
                  <li className={classes.ExternalQueryTabWrapper}>
                    <div className={classes.ExternalQueryWrapper}>
                      {externalQuery}
                    </div>
                  </li>
                )
                : null
            }
          </TabList>
      }
      {
        tabData.map((item, index) =>
          <TabPanel
            className={classes.TabPanel}
            disabled={isDisabled}
            forceRender={isInvisiblePanel}
            key={index}
            selectedClassName={classes.TabPanelSelected}
          >
            <div
              className={
                isInvisiblePanel
                  ? visibilityPanelStyle(index, selectedTabIndex)
                  : classes.TabPanelChildrenBase
              }
            >
              {item.child}
            </div>
          </TabPanel>
        )
      }
    </ReactTabs>
  )
}

export default VerticalTabs
